import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton, iconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { IconPrinter, IconRefresh, IconSettings } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useId, useMemo } from "react";
import { ToggleButton } from "react-aria-components";
import { useParams } from "react-router";
import type { ReportDataStreamState } from "../../api/entities/performanceReport.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { selectCompany } from "../../features/company/companySlice.js";
import type { ReportRenderProps } from "../../features/performanceReports/baseReport.js";
import { ComplianceReport } from "../../features/performanceReports/complianceReport.js";
import { InsuranceUpsellOutcomeReport } from "../../features/performanceReports/insuranceUpsellOutcomeReport.js";
import { PerformanceReport } from "../../features/performanceReports/performanceReport.js";
import { ReportCard } from "../../features/performanceReports/reportCard.js";
import { ReportLayoutSkeleton } from "../../features/performanceReports/reportLayoutSkeleton.js";
import { useAppSelector } from "../../hooks/hook.js";
import "./reportDetailPage.css";

export interface ReportDetailPageProps {
  // ...
}

export const ReportDetailPage: FC<ReportDetailPageProps> = (props) => {
  const { reportId: reportIdParam, playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId URL param");
  }

  const playbookId = asPlaybookId(playbookIdParam);

  const renderReport = (renderProps: ReportRenderProps) => (
    <ReportView playbookId={playbookId} {...renderProps} />
  );

  switch (reportIdParam) {
    case "template:insurance-upsell-outcome":
      return (
        <InsuranceUpsellOutcomeReport>
          {renderReport}
        </InsuranceUpsellOutcomeReport>
      );

    case "template:performance":
      return (
        <PerformanceReport playbookId={playbookId}>
          {renderReport}
        </PerformanceReport>
      );

    case "template:compliance":
    default:
      return (
        <ComplianceReport playbookId={playbookId}>
          {renderReport}
        </ComplianceReport>
      );
  }
};

interface ReportViewProps extends ReportRenderProps {
  playbookId: PlaybookId;
}

const ReportView: FC<ReportViewProps> = ({
  title,
  isScopedToPlaybook,
  settings,
  isSettingsExpanded = true,
  onSettingsToggle,
  renderFilterBar,
  onRefresh,
  state,
  playbookId,
}) => {
  const company = useAppSelector(selectCompany);
  const { data: playbook } = usePlaybookByIdQuery(playbookId);
  const rechartSyncId = useId();
  const generatedAt = useEarliestCardGeneratedAt(state);

  const hasSettings = isScopedToPlaybook || settings;

  return (
    <PageLayout className="report-print-root">
      <PageHeader
        title={title}
        eyebrow={
          <div className="flex items-baseline justify-between gap-x-4">
            <div>
              {company?.name ? <span>{company?.name}</span> : null}
              {company?.name && isScopedToPlaybook ? (
                <span> &middot; </span>
              ) : null}
              {isScopedToPlaybook ? <span>{playbook?.name}</span> : null}
            </div>

            {generatedAt ? (
              <div>
                Generated {generatedAt.toLocaleString(DateTime.DATETIME_MED)}
              </div>
            ) : null}
          </div>
        }
        actions={
          onSettingsToggle && hasSettings ? (
            <div data-report-screen-only>
              <ToggleButton
                aria-label="Toggle report settings"
                className={iconButton({
                  variant: ButtonVariant.Tertiary,
                  className: "selected:bg-slate-200",
                })}
                isSelected={isSettingsExpanded}
                onClick={onSettingsToggle}
              >
                <IconSettings className="size-5 shrink-0 text-foreground" />
              </ToggleButton>
            </div>
          ) : null
        }
        filterBar={
          <div>
            {renderFilterBar({
              rightActions: (
                <>
                  <IconButton
                    variant={ButtonVariant.Tertiary}
                    size={Size.SMALL}
                    icon={<IconRefresh className="size-4" />}
                    accessibleLabel="Refresh report"
                    onClick={onRefresh}
                  />
                  <IconButton
                    variant={ButtonVariant.Tertiary}
                    size={Size.SMALL}
                    icon={<IconPrinter className="size-4" />}
                    accessibleLabel="Print report"
                    onClick={() => window.print()}
                  />
                </>
              ),
            })}
          </div>
        }
      />

      <PageBody>
        {isSettingsExpanded && hasSettings ? (
          <div
            data-report-screen-only
            className="flex flex-wrap items-center gap-2"
          >
            {isScopedToPlaybook ? (
              <PerformancePlaybookSwitcher staysOnFullPath />
            ) : null}

            {settings}
          </div>
        ) : null}

        {state?.layout ? (
          state.layout.sections.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="grid grid-cols-1 gap-6"
              style={{
                gridTemplateColumns: `repeat(${section.columnCount}, minmax(0, 1fr))`,
              }}
            >
              {section.cards.map((card) => (
                <div
                  key={card.id}
                  className={
                    card.type === "table" ? undefined : "report-print-card"
                  }
                >
                  <ReportCard
                    card={card}
                    reportState={state}
                    rechartSyncId={rechartSyncId}
                  />
                </div>
              ))}
            </div>
          ))
        ) : (
          <ReportLayoutSkeleton />
        )}
      </PageBody>
    </PageLayout>
  );
};

/** Earliest `generatedAt` across loaded cards, reflecting how stale the oldest data is. */
function useEarliestCardGeneratedAt(
  state: ReportDataStreamState | undefined,
): DateTime | undefined {
  return useMemo(() => {
    const timestamps = Object.values(state?.data ?? {})
      .flatMap((card) => (card ? [DateTime.fromISO(card.generatedAt)] : []))
      .filter((dateTime) => dateTime.isValid);

    if (timestamps.length === 0) {
      return undefined;
    }

    return DateTime.min(...timestamps);
  }, [state?.data]);
}
